import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';

type DriverTheme = 'dark' | 'light';

interface DriverThemeTokens {
  bgPage: string;
  bgCard: string;
  textPrimary: string;
  textSecondary: string;
  textAccent: string;
  border: string;
  bgPageTw: string;
  bgCardTw: string;
  textPrimaryTw: string;
  textSecondaryTw: string;
  borderTw: string;
}

const DARK_TOKENS: DriverThemeTokens = {
  bgPage: '#12100E',
  bgCard: '#1F1A17',
  textPrimary: '#EDE9E5',
  textSecondary: '#9E8E82',
  textAccent: '#FB8C3C',
  border: '#372E27',
  bgPageTw: 'bg-[#12100E]',
  bgCardTw: 'bg-[#1F1A17]',
  textPrimaryTw: 'text-[#EDE9E5]',
  textSecondaryTw: 'text-[#9E8E82]',
  borderTw: 'border-[#372E27]',
};

const LIGHT_TOKENS: DriverThemeTokens = {
  bgPage: '#F9F7F4',
  bgCard: '#FFFFFF',
  textPrimary: '#302318',
  textSecondary: '#7E7168',
  textAccent: '#F97316',
  border: '#EAE6E1',
  bgPageTw: 'bg-[#F9F7F4]',
  bgCardTw: 'bg-white',
  textPrimaryTw: 'text-[#302318]',
  textSecondaryTw: 'text-[#7E7168]',
  borderTw: 'border-[#EAE6E1]',
};

interface DriverThemeContextType {
  theme: DriverTheme;
  tokens: DriverThemeTokens;
  isDark: boolean;
  toggleTheme: () => void;
}

const DriverThemeContext = createContext<DriverThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'app-theme';

export function DriverThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<DriverTheme>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(STORAGE_KEY) as DriverTheme) || 'light';
    }
    return 'light';
  });

  // Sync dark class on <html> for Tailwind dark: utilities and CSS variables
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  const isDark = theme === 'dark';
  const tokens = isDark ? DARK_TOKENS : LIGHT_TOKENS;

  const value = useMemo(() => ({ theme, tokens, isDark, toggleTheme }), [theme, tokens, isDark]);

  return (
    <DriverThemeContext.Provider value={value}>
      {children}
    </DriverThemeContext.Provider>
  );
}

export function useDriverTheme() {
  const context = useContext(DriverThemeContext);
  if (!context) {
    throw new Error('useDriverTheme must be used within a DriverThemeProvider');
  }
  return context;
}

// Alias for project-wide usage
export const useAppTheme = useDriverTheme;
